/* Aurio — money math.
   Every pure, framework-free calculation the app runs on: date and
   statement-cycle arithmetic, the payment ledger, real interest accrual, and
   the payoff/savings/investment projections. No React, no theme, no
   formatting — those stay in src/aurio.jsx, so everything here is directly
   unit-testable (see money.test.js) and has no import cycle back to the UI.

   Extracted from aurio.jsx in v0.25.0 as a pure move — the first slice of the
   long-planned module split. */

/* Card issuers set the minimum at ~1–3% of the statement balance. We default
   new cards to 3% (the higher, safer end) with a $25 floor so tiny balances
   don't produce a near-zero minimum, capped at the balance itself. Only
   meaningful for type "card" — loans keep fixed, negotiated minimums. */
const AUTO_MIN_PCT = 0.03;
const AUTO_MIN_FLOOR = 25;
const autoMinPayment = (balance) => balance <= 0 ? 0 : Math.min(balance, Math.max(AUTO_MIN_FLOOR, Math.round(balance * AUTO_MIN_PCT)));
/* Keeps an auto-minimum card's minPayment in sync with its balance whenever
   the balance changes (payment logged, charge logged, interest posted,
   balance edited). Manual debts (or non-card types) pass through untouched. */
const withAutoMin = (d) => (d.type === "card" && d.minPaymentAuto) ? { ...d, minPayment: autoMinPayment(d.balance) } : d;

const monthName = (d, lang) => d.toLocaleDateString(lang || "en-NZ", { month: "long", year: "numeric" });
const addMonths = (date, m) => { const d = new Date(date); d.setMonth(d.getMonth() + m); return d; };
/* Dates are formatted from LOCAL calendar parts, never toISOString(): UTC+13
   (NZ daylight saving — Aurio's home market) puts noon local at 23:00 the
   previous day in UTC, so toISOString().slice(0,10) reports yesterday for
   most of the working day. That silently mis-dated every expense and payment,
   and shifts a statement cycle by a day. Reading back stays at noon local
   (`parseISO`), which is what these strings are paired with everywhere. */
const pad2 = (n) => String(n).padStart(2, "0");
const localISO = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const todayISO = () => localISO(new Date());
const uid = () => Math.random().toString(36).slice(2, 9);
/* Monthly-equivalent weight of each cadence. `once` is 0: a one-off cost has
   no monthly rate, and averaging it into one would be the exact smearing this
   forecast exists to stop. It is counted on its real date instead. */
const FREQ_FACTOR = { weekly: 52 / 12, fortnightly: 26 / 12, monthly: 1, quarterly: 1 / 3, annual: 1 / 12, once: 0 };
/* Cadences a bill can have, in the order they're offered. Single source so the
   pickers can't drift apart from the maths. */
const FREQS = ["weekly", "fortnightly", "monthly", "quarterly", "annual", "once"];
const monthlyIncomeOf = (s) => (s.income || 0) * (FREQ_FACTOR[s.incomeFreq] || 1);
const recMonthly = (r) => r.amount * (FREQ_FACTOR[r.freq] || 1);

/* ═══ statement cycles ═══
   Debts accrue interest over a billing cycle, not a calendar month. A cycle
   closes on `statementDay` and the payment falls due on `dueDay` — both
   clamped to 1–28 so the day exists in every month (the same convention as
   state.payday). A debt the user hasn't dated falls back to statementDay 1,
   i.e. calendar-month cycles, which is exactly how accrual behaved before
   dates existed — so nothing shifts under an undated debt.

   ISO dates are always read back at noon local (`parseISO`) and written from
   a noon-local Date, which keeps the calendar day stable either side of UTC. */
const CYCLE_MAX_DAY = 28;
/* Typical gap between a card's statement closing and its payment due date.
   Used only to pre-fill the statement day so the form can ask for one date
   instead of two — the user can override it. */
const STATEMENT_LEAD_DAYS = 21;
const clampDay = (n) => Math.min(CYCLE_MAX_DAY, Math.max(1, Math.round(Number(n) || 1)));
const wrapDay = (n) => ((Math.round(n) - 1 + CYCLE_MAX_DAY * 2) % CYCLE_MAX_DAY) + 1;
const derivedStatementDay = (dueDay) => wrapDay(clampDay(dueDay) - STATEMENT_LEAD_DAYS);
const statementDayOf = (d) => d.statementDay != null ? clampDay(d.statementDay)
  : d.dueDay != null ? derivedStatementDay(d.dueDay) : 1;
const dateISO = (y, m, day) => localISO(new Date(y, m, day, 12));
const parseISO = (iso) => new Date(iso + "T12:00");
const daysBetween = (fromISO, toISO) => Math.round((parseISO(toISO) - parseISO(fromISO)) / 86400000);
/* Roll a recurring item's due date to its next occurrence. Shared by the
   auto-logger and the upcoming-payments list so the two can never disagree
   about when a bill lands. Stays on localISO: parseISO builds *local* noon,
   and east of UTC (NZ at +13) that is the previous day in UTC — going via
   toISOString would silently shift every rolled date a day early. */
const advanceDue = (iso, freq) => {
  // A one-off never recurs: null is the "no next occurrence" signal that
  // processRecurring and upcomingBills both stop on.
  if (freq === "once") return null;
  const d = parseISO(iso);
  const months = { monthly: 1, quarterly: 3, annual: 12 }[freq];
  if (months) d.setMonth(d.getMonth() + months);
  else d.setDate(d.getDate() + (freq === "weekly" ? 7 : 14));
  return localISO(d);
};
/* Next occurrence of a day-of-month, today counting as due today. */
function nextDayOfMonth(day, fromISO = todayISO()) {
  const d = parseISO(fromISO);
  const dd = clampDay(day);
  return d.getDate() <= dd ? dateISO(d.getFullYear(), d.getMonth(), dd)
    : dateISO(d.getFullYear(), d.getMonth() + 1, dd);
}
/* The statement close on or before `iso`. */
function closeOnOrBefore(iso, day) {
  const d = parseISO(iso);
  return d.getDate() >= day ? dateISO(d.getFullYear(), d.getMonth(), day)
    : dateISO(d.getFullYear(), d.getMonth() - 1, day);
}
/* Every statement close strictly after `afterISO` and on or before
   `throughISO`, oldest first. Capped like the other catch-up loops so a
   wildly stale bookmark can't spin. */
function statementCloses(afterISO, throughISO, day, cap = 60) {
  const out = [];
  let c = closeOnOrBefore(throughISO, day);
  while (c > afterISO && out.length < cap) {
    out.unshift(c);
    const d = parseISO(c);
    c = dateISO(d.getFullYear(), d.getMonth() - 1, day);
  }
  return out;
}

/* Ledger entry helpers. A "credit" entry (charge, posted interest) grew the
   balance; a payment shrank it. Legacy rows carry no type — those are
   payments. */
/* The two payoff strategies, defined once. `byStrategy` is the important
   half: it is the single answer to "which debt do we attack first", and
   simulate(), applyLump() and the Debts tab's target highlight all have to
   give the SAME answer. Restating the comparator in each of them (as this
   code did) meant a windfall could be applied to a different debt than the
   plan was attacking, with the UI pointing at a third. */
const STRATEGIES = ["avalanche", "snowball"];
const AVALANCHE = STRATEGIES[0];
const otherStrategy = (s) => (s === AVALANCHE ? STRATEGIES[1] : AVALANCHE);
const byStrategy = (strategy) => (a, b) =>
  strategy === AVALANCHE ? b.apr - a.apr : a.balance - b.balance;

const isCreditEntry = (p) => p.type === "charge" || p.type === "interest";
const isPaymentEntry = (p) => p.type === "payment" || !p.type;
/* The balance immediately before an entry was applied. `balanceBefore` is
   recorded at write time and is authoritative — an overpayment clamped to 0
   can't be undone by arithmetic. Rows predating that field fall back to
   reversing the amount. */
const balanceBeforeEntry = (p, balanceAfter) =>
  p.balanceBefore != null ? p.balanceBefore : balanceAfter + (isCreditEntry(p) ? -p.amount : p.amount);

/* Brings a debt up to the current shape — run on load, on save, and on the
   onboarding draft so there is one definition of a well-formed debt.

   The accrual bookmark is stamped to the CURRENT statement close whenever
   it's missing (a new debt, or data predating statement cycles), so a debt
   can never be charged interest for time before Aurio knew about it. The old
   calendar-month bookmark migrates directly: `lastInterestMonth: "2026-07"`
   meant interest was posted through the boundary at the start of that month,
   i.e. 2026-07-01. The first cycle after migration is therefore a short one
   (statement close minus the 1st), which under-charges slightly rather than
   double-charging — the safe direction for a one-off transition. */
function normalizeDebt(d, today = todayISO()) {
  const day = (v) => (v == null || v === "" ? null : clampDay(v));
  const next = { ...d, dueDay: day(d.dueDay), statementDay: day(d.statementDay) };
  delete next.lastInterestMonth;
  if (!next.lastInterestDate) {
    next.lastInterestDate = d.lastInterestMonth
      ? `${d.lastInterestMonth}-01`
      : closeOnOrBefore(today, statementDayOf(next));
  }
  if (next.lastStatementBalance == null) next.lastStatementBalance = d.balance || 0;
  return next;
}

/* What this plan started at, so "x% destroyed" has a real denominator.

   `startingDebt` was written once during onboarding and never revisited, so
   anyone who skipped onboarding (or added their debts afterwards) kept a
   baseline of 0 — and 0 makes the progress bar read 0% forever no matter how
   much they repay. Deriving it instead means it can't go stale: each debt
   contributes the balance it had before its earliest recorded activity, or
   today's balance if nothing has been logged against it yet.

   Taking on a NEW debt therefore raises the baseline and today's total by the
   same amount, which correctly neither invents progress nor erases what came
   before. The stored value is kept as a floor: someone who has been editing
   balances by hand rather than logging payments has real progress the ledger
   can't see, and that shouldn't be zeroed out by switching to a derived
   figure. */
function derivedStartingDebt(state) {
  const byDebt = new Map();
  for (const p of state.payments || []) {
    if (p.balanceBefore == null) continue;
    const cur = byDebt.get(p.debtId);
    if (!cur || (p.date || "") < (cur.date || "")) byDebt.set(p.debtId, p);
  }
  const derived = (state.debts || []).reduce((sum, d) => {
    const first = byDebt.get(d.id);
    return sum + (first ? first.balanceBefore : (d.balance || 0));
  }, 0);
  return Math.max(Number(state.startingDebt) || 0, derived);
}

/* payoff engine. neverPaysOff: true when the balance is still growing (or
   flat) after the 600-month simulation cap — combined minimums + extra
   can't outpace interest at this APR, so freeDate/totalInterest are
   simulation artifacts of the cap, not a real projection, and callers
   should show a warning instead of the number. */
function simulate(debts, extra, strategy) {
  let list = debts.filter(d => d.balance > 0).map(d => ({ ...d }));
  if (!list.length) return { months: 0, totalInterest: 0, curve: [0], freeDate: new Date(), neverPaysOff: false };
  let months = 0, totalInterest = 0;
  const curve = [list.reduce((s, d) => s + d.balance, 0)];
  while (list.some(d => d.balance > 0) && months < 600) {
    months++;
    for (const d of list) {
      const i = d.balance * (d.apr / 100 / 12);
      d.balance += i;
      totalInterest += i;
    }
    /* Every month the same total leaves your pocket: the extra, plus each
       debt's minimum. When a debt clears (or needs less than its minimum to
       finish), the unspent remainder rolls into the pool and attacks the next
       debt in strategy order. That roll-over IS avalanche/snowball — without
       it a cleared debt's payment silently evaporates and the plan reads far
       slower than reality (one real account: 37 months instead of 17). */
    let pool = extra;
    for (const d of list) {
      const pay = Math.min(d.minPayment, d.balance);
      d.balance -= pay;
      pool += d.minPayment - pay;
    }
    const order = [...list].filter(d => d.balance > 0).sort(byStrategy(strategy));
    for (const d of order) {
      if (pool <= 0) break;
      const pay = Math.min(pool, d.balance);
      d.balance -= pay;
      pool -= pay;
    }
    curve.push(Math.max(0, list.reduce((s, d) => s + Math.max(0, d.balance), 0)));
  }
  const neverPaysOff = list.some(d => d.balance > 0);
  return { months, totalInterest, curve, freeDate: addMonths(new Date(), months), neverPaysOff };
}

/* 12-month forward "money map": how much is committed each month (bills +
   active debt minimums + the discretionary extra payment while still in
   debt) versus how much is saveable. A debt's own minPayment drops out of
   "committed" the month after its explicit endDate — a fixed-term loan
   (e.g. a car payment) you already know the end of. The extraPayment drops
   out once the CURRENT payoff plan (sim) says every debt is gone — reusing
   sim.months keeps this consistent with the real avalanche/snowball engine
   instead of guessing. Bills are the flat monthly-equivalent of existing
   recurring items — this doesn't model annual/seasonal bills yet, so it
   deliberately never fabricates a "big bill" spike for those. */
function moneyMap(state, sim, monthlyIncome, totalBudget, horizonMonths = 12) {
  const now = new Date();
  const firstMonth = new Date(now.getFullYear(), now.getMonth(), 1, 12);
  const lastMonth = new Date(now.getFullYear(), now.getMonth() + horizonMonths, 1, 12);

  /* Bills land on their real dates. The old version averaged everything into a
     flat monthly figure, which is precisely the failure this forecast exists to
     fix: smear a $890 annual insurance across twelve months and the month it
     actually lands looks affordable. Occurrences are walked with advanceDue —
     the same helper upcomingBills uses — so the planner and the forecast can
     never disagree about when a bill falls. */
  const billsByMonth = new Map();
  /* A commitment with no date can't be placed on the calendar, but it is still
     money owed — dropping it would understate every month. Those fall back to
     their monthly equivalent, spread as before. A spent `once` item correctly
     contributes nothing: it has already happened. */
  let undatedMonthly = 0;
  for (const r of state.recurring || []) {
    if (!r.nextDue) {
      if (r.freq !== "once") undatedMonthly += recMonthly(r);
      continue;
    }
    let due = r.nextDue;
    for (let guard = 0; guard < 800; guard++) {
      const d = parseISO(due);
      if (d >= lastMonth) break;
      if (d >= firstMonth) {
        const key = due.slice(0, 7);
        const bucket = billsByMonth.get(key) || { total: 0, items: [] };
        bucket.total += r.amount;
        bucket.items.push({ id: r.id, name: r.name, amount: r.amount, freq: r.freq, date: due });
        billsByMonth.set(key, bucket);
      }
      const next = advanceDue(due, r.freq);
      if (!next || next <= due) break;
      due = next;
    }
  }

  const months = [];
  for (let i = 0; i < horizonMonths; i++) {
    const monthStart = new Date(now.getFullYear(), now.getMonth() + i, 1, 12);
    const key = monthKeyOf(monthStart);
    const debtsCommitted = state.debts.reduce((s, d) => {
      if (d.balance <= 0) return s;
      if (d.endDate && parseISO(d.endDate) < monthStart) return s; // this debt's own end date has passed
      return s + d.minPayment;
    }, 0);
    const extraStillCommitted = i < sim.months;
    const bucket = billsByMonth.get(key) || { total: 0, items: [] };
    const committed = bucket.total + undatedMonthly + debtsCommitted + (extraStillCommitted ? state.extraPayment : 0);
    /* Signed on purpose. This used to be Math.max(0, …), which rendered the
       month you cannot cover as a flat zero — hiding the one thing anybody
       opens a forecast to find. A negative saveable IS the answer. */
    const saveable = monthlyIncome - totalBudget - committed;
    /* What made this month heavy: the lumpy bills, biggest first. A warning
       that can't name its cause isn't actionable. */
    const causes = [...bucket.items]
      .filter(b => b.freq !== "weekly" && b.freq !== "fortnightly" && b.freq !== "monthly")
      .sort((a, b) => b.amount - a.amount);
    months.push({
      key, monthStart, committed, saveable,
      shortfall: saveable < 0 ? -saveable : 0,
      bills: bucket.items, causes,
    });
  }
  return months;
}

/* ── what-if scenarios (Premium) ──────────────────────────────────────────
   A scenario is a set of DELTAS applied to the position the user has already
   declared, re-run through the very same simulate() and moneyMap() the real
   plan uses. Never a parallel model: a scenario that disagreed with the plan
   it is compared against would be worse than no scenario at all.

   Levers, and why these five:
     extra    — the only debt lever the user directly controls each month
     lump     — a windfall (tax refund, bonus) applied once, right now
     income   — a raise, a lost shift, a partner going back to work
     spend    — cutting or adding to the living budget
     strategy — avalanche vs snowball, the other real choice

   The honesty rule that makes this more than a scenario toy: an extra payment
   clears debt faster in EVERY model, so a modeller that only reports the new
   date will happily sell a fantasy. Aurio re-walks the calendar under the
   scenario and reports whether the user's own cash flow can actually fund it —
   `affordable`, `tightMonths` and `worstShortfall` are as much the answer as
   the date is. */
const EMPTY_SCENARIO = { extra: 0, lump: 0, income: 0, spend: 0, strategy: null };

/* Spend a lump sum the way simulate() itself would: against the strategy's
   target debt first, overflowing to the next as each clears. Sharing the
   ordering rule (rather than restating it) is what keeps a $2,000 windfall
   landing on the same debt the plan says to attack. */
function applyLump(debts, lump, strategy) {
  const list = debts.map(d => ({ ...d }));
  if (!(lump > 0)) return list;
  let rest = lump;
  const order = list.filter(d => d.balance > 0).sort(byStrategy(strategy));
  for (const d of order) {
    if (rest <= 0) break;
    const pay = Math.min(rest, d.balance);
    d.balance -= pay;
    rest -= pay;
  }
  return list;
}

/* Summarise one side of the comparison. Kept private so base and scenario can
   never be measured by two different rulers. */
function scenarioOutcome(state, debts, extra, strategy, monthlyIncome, totalBudget, horizonMonths) {
  const sim = simulate(debts, extra, strategy);
  const map = moneyMap({ ...state, debts, extraPayment: extra }, sim, monthlyIncome, totalBudget, horizonMonths);
  const short = map.filter(m => m.shortfall > 0);
  return {
    months: sim.months,
    totalInterest: sim.totalInterest,
    freeDate: sim.freeDate,
    neverPaysOff: sim.neverPaysOff,
    tightMonths: short.length,
    worstShortfall: short.reduce((mx, m) => Math.max(mx, m.shortfall), 0),
    avgSaveable: map.reduce((s, m) => s + Math.max(0, m.saveable), 0) / (map.length || 1),
    map,
  };
}

/* `delta` figures are signed so that POSITIVE always means better: months and
   interest saved, tight months removed. A scenario that makes things worse
   reports negatives, and the UI can render one rule rather than four. */
function whatIf(state, scenario, monthlyIncome, totalBudget, horizonMonths = 12) {
  const sc = { ...EMPTY_SCENARIO, ...(scenario || {}) };
  const baseStrategy = state.strategy;
  const strategy = sc.strategy || baseStrategy;
  const debts = state.debts || [];

  const base = scenarioOutcome(state, debts, state.extraPayment || 0, baseStrategy,
    monthlyIncome, totalBudget, horizonMonths);

  /* A lump sum is money that leaves once, now — it changes the balances the
     simulation starts from, not the monthly commitment. */
  const scenDebts = applyLump(debts, sc.lump, strategy);
  const scenExtra = Math.max(0, (state.extraPayment || 0) + sc.extra);
  const scen = scenarioOutcome(state, scenDebts, scenExtra, strategy,
    monthlyIncome + sc.income, Math.max(0, totalBudget + sc.spend), horizonMonths);

  const touched = sc.extra !== 0 || sc.lump !== 0 || sc.income !== 0 || sc.spend !== 0
    || (sc.strategy != null && sc.strategy !== baseStrategy);

  return {
    base, scenario: scen, touched, strategy,
    delta: {
      months: base.months - scen.months,
      totalInterest: base.totalInterest - scen.totalInterest,
      tightMonths: base.tightMonths - scen.tightMonths,
    },
    /* Can the user actually live this scenario? No month in the horizon may go
       negative. Note this is judged on the SCENARIO's own cash flow, so a plan
       that was already tight is not excused by having been tight before. */
    affordable: scen.tightMonths === 0,
    addedTightMonths: scen.tightMonths - base.tightMonths,
  };
}

/* First-home deposit planner (Grow tab). Country-specific rules govern how
   much of a KiwiSaver/Super balance actually counts toward the deposit:
     NZ — KiwiSaver first-home withdrawal must leave $1,000 in the account
          (no upper limit otherwise)
     AU — First Home Super Saver Scheme caps usable contributions at $50,000
          lifetime
     other — no scheme modelled; the whole balance counts as plain savings
   `monthlySaveable` is the same figure the money map already computes
   (moneyMap()'s average "saveable" — reused, not recomputed) so this planner
   and the savings-rate headline never disagree about how fast money piles up.
   `months: null` means "can't project a date" (nothing saveable) rather than
   fabricating one — the same honesty rule simulate()'s neverPaysOff follows. */
function schemeUsable(scheme, country) {
  if (country === "NZ") return Math.max(0, (scheme || 0) - 1000);
  if (country === "AU") return Math.min(scheme || 0, 50000);
  return scheme || 0;
}

function homePlan({ price, depositPct, cash, scheme }, country, monthlySaveable, today = new Date()) {
  const depositNeeded = Math.max(0, (price || 0) * (depositPct || 0) / 100);
  const usable = schemeUsable(scheme, country);
  const available = (cash || 0) + usable;
  const gap = Math.max(0, depositNeeded - available);
  const months = monthlySaveable > 0 ? Math.ceil(gap / monthlySaveable) : (gap <= 0 ? 0 : null);
  const readyDate = months != null ? addMonths(today, months) : null;
  return { depositNeeded, schemeUsable: usable, available, gap, months, readyDate };
}

/* Priority waterfall for one pot of spare money.

   Before this, every destination in the Grow tab was projected as if it
   received 100% of the monthly saveable amount: the house deposit assumed all
   of it, the savings-goal nudge assumed all of it, and nothing reconciled
   them. Both dates were stated as fact and both could not be true.

   Money now flows in a single order — fill the first destination, then the
   next — which mirrors simulate(): pay every minimum, then apply the pool in
   priority order. Same mental model the user already learned on the Debts tab.

   `destinations` are ordered, each { id, need, already, targetDate?, ongoing? }.
   An `ongoing` destination (investing) is never "complete"; it simply starts
   once everything above it is funded and absorbs the remainder from then on.

   Returns per destination:
     startMonth  months from now when money first reaches it
     months      months from now when it completes (null = never at this rate,
                 or ongoing) — never a fabricated date, matching
                 simulate()'s neverPaysOff and homePlan()
     readyDate   Date | null
     late        true when it completes after its own targetDate */
function allocate(destinations, monthlySaveable, today = new Date()) {
  const out = [];
  let elapsed = 0;                       // months consumed by earlier destinations
  let exhausted = monthlySaveable <= 0;  // nothing to distribute at all

  for (const d of destinations || []) {
    const need = Math.max(0, (d.need || 0) - (d.already || 0));

    if (d.ongoing) {
      // Starts when the queue ahead of it clears; no completion date.
      out.push({
        id: d.id, startMonth: exhausted ? null : elapsed, months: null,
        readyDate: null, remaining: need, late: false, ongoing: true,
      });
      continue;
    }

    if (need <= 0) {
      // Already funded — consumes no time and does not delay anything below.
      out.push({
        id: d.id, startMonth: elapsed, months: elapsed,
        readyDate: addMonths(today, elapsed), remaining: 0, late: false, ongoing: false,
      });
      continue;
    }

    if (exhausted) {
      out.push({ id: d.id, startMonth: null, months: null, readyDate: null, remaining: need, late: false, ongoing: false });
      continue;
    }

    const takes = Math.ceil(need / monthlySaveable);
    const startMonth = elapsed;
    elapsed += takes;
    const readyDate = addMonths(today, elapsed);
    const late = Boolean(d.targetDate) && readyDate > new Date(d.targetDate + "T12:00");
    out.push({ id: d.id, startMonth, months: elapsed, readyDate, remaining: need, late, ongoing: false });
  }
  return out;
}

/* Emergency fund sizing — no new user input, built entirely from state
   already on the Budget and Debts tabs.

   "All recurring bills" is deliberately not filtered by category: a bill the
   user chose to automate (rent, insurance, power) is overwhelmingly the kind
   of thing that still has to be paid if income stops, and asking the user to
   tag each one essential/non-essential would be new setup friction for a
   number that's meant to need none. Debt minimums count for the same reason
   — losing your income doesn't cancel them. */
const ESSENTIAL_CATEGORIES = ["groceries", "transport", "rent", "utilities", "insurance"];

/* The living-budget total. Only categories that STILL EXIST count.
   Deleting a custom category reassigned its expenses but left its amount
   behind in `state.budgets`, and the total summed that object's raw values —
   so "Living budget" was larger than the rows on the Budget page added up to.
   On the account that surfaced it: $4,439 shown against $3,579 visible, $860
   of it belonging to two deleted categories. That gap was not cosmetic. It
   flowed straight into `leftover`, the money map's committed figure, every
   savings-goal and deposit date through the allocation waterfall, and the
   what-if comparison — all of them understating what the user could actually
   save, every month, by the same $860.

   Summing over the category list rather than the budget object means the
   headline can never disagree with the rows beneath it, and state that is
   already orphaned corrects itself without a destructive migration. The
   orphaned amounts stay in storage, harmless and uncounted, rather than being
   deleted out from under anyone. */
/* What is still owed, in one place. This was computed four separate times in
   the app and two of them clamped at zero while two did not, so a credit
   balance (an overpaid card) would have shown as a DIFFERENT total depending on
   which screen you were looking at. Clamping is the right reading and the one
   the rest of the engine already uses: simulate() filters `balance > 0` and
   moneyMap() skips `balance <= 0`, so money sitting in credit on one card has
   never reduced what the other cards cost you. */
function totalOwed(debts) {
  return (debts || []).reduce((sum, d) => sum + Math.max(0, d.balance || 0), 0);
}

function budgetTotal(budgets, categoryIds) {
  const live = new Set(categoryIds || []);
  let total = 0;
  for (const [id, amount] of Object.entries(budgets || {})) {
    if (live.has(id)) total += amount || 0;
  }
  return total;
}

function essentialMonthly(state) {
  const budgetEssentials = ESSENTIAL_CATEGORIES.reduce((s, id) => s + ((state.budgets && state.budgets[id]) || 0), 0);
  const billsMonthly = (state.recurring || []).reduce((s, r) => s + recMonthly(r), 0);
  const debtMins = (state.debts || []).reduce((s, d) => s + (d.balance > 0 ? (d.minPayment || 0) : 0), 0);
  return budgetEssentials + billsMonthly + debtMins;
}

/* `months` follows the standard 3–6 month convention; the app uses 3 as the
   floor for "fully funded" (see nextStep) rather than picking a number more
   specific than the convention itself, which would pretend to know something
   about the user's job security that it doesn't. */
function emergencyTarget(state, months = 3) {
  return essentialMonthly(state) * months;
}

/* Standard, defensible personal-finance sequencing for "what should my next
   dollar do", evaluated top-down against real state. Only the FIRST unmet
   step is ever presented as the recommendation; the rest are context. This
   function is intentionally decoupled from allocate()/buildDestinations — it
   takes the emergency fund's current balance as a plain number so it can be
   tested (and reasoned about) without constructing a destination list. */
function nextStep(state, emergencySaved, expReturn) {
  const essential = essentialMonthly(state);
  const starterTarget = essential;              // 1 month
  const fullTarget = emergencyTarget(state, 3);  // 3 months (floor of the 3–6 convention)

  const worstDebt = (state.debts || [])
    .filter(d => d.balance > 0 && (d.apr || 0) > (expReturn ?? 7))
    .sort((a, b) => (b.apr || 0) - (a.apr || 0))[0] || null;

  const hasDatedGoal = (state.savingsGoals || []).some(g => g.targetDate) || Boolean(state.home && state.home.active);

  const steps = [
    { id: "starter", done: essential <= 0 || emergencySaved >= starterTarget, target: starterTarget },
    { id: "debt", done: !worstDebt, debt: worstDebt },
    { id: "fullBuffer", done: essential <= 0 || emergencySaved >= fullTarget, target: fullTarget },
    { id: "goals", done: !hasDatedGoal },
    { id: "invest", done: false }, // the fallback; never "done" so there's always a next step
  ];
  return { steps, next: steps.find(s => !s.done) };
}

/* investment projection: monthly compounding, optional contribution
   boost after `boostFromMonth` (debt payments redirected to investing).

   `inflation` (annual %) is optional and additive: when given, `realFinal` and
   `realCurve` restate the same projection in TODAY's money. A 24-year
   projection compounding at a nominal 7% and compared against a nominal
   target overstates what the money actually buys — $4,000/mo in 24 years at
   2.5% inflation has the purchasing power of about $2,200 today. Existing
   callers pass nothing and are unaffected. */
function project(current, monthly, boostAmount, boostFromMonth, months, annualReturn, inflation = 0) {
  const r = annualReturn / 100 / 12;
  const i = (inflation || 0) / 100 / 12;
  let bal = current;
  const curve = [bal];
  const realCurve = [bal];
  for (let m = 1; m <= months; m++) {
    bal = bal * (1 + r) + monthly + (boostFromMonth != null && m > boostFromMonth ? boostAmount : 0);
    curve.push(bal);
    realCurve.push(i > 0 ? bal / Math.pow(1 + i, m) : bal);
  }
  return { final: bal, curve, realFinal: realCurve[realCurve.length - 1], realCurve };
}

/* recurring auto-log */
function processRecurring(state) {
  const today = todayISO();
  let expenses = [...state.expenses];
  let logged = 0;
  const recurring = (state.recurring || []).map(r => {
    let next = r.nextDue;
    let guard = 0;
    while (next && next <= today && guard < 60) {
      expenses.unshift({ id: uid(), amount: r.amount, category: r.category, note: `${r.name} (auto)`, date: next, recurringId: r.id });
      next = advanceDue(next, r.freq);
      logged++; guard++;
    }
    return { ...r, nextDue: next };
  });
  /* The "paid ahead" marker is keyed to one specific occurrence
     (billId:dueDate). The planner lists every occurrence inside its window,
     so a tick can legitimately sit on an occurrence weeks out — keep any
     tick that hasn't happened yet (date >= the item's current nextDue) and
     drop the ones already auto-logged above, so the map can't grow forever
     and a fresh occurrence always starts unchecked. Matching on equality
     here would silently wipe a tick on any occurrence but the next one. */
  const nextDueById = new Map(recurring.map(r => [r.id, r.nextDue]));
  const billsPaidAhead = {};
  for (const [k, v] of Object.entries(state.billsPaidAhead || {})) {
    const cut = k.lastIndexOf(":");
    if (cut < 0) continue;
    const next = nextDueById.get(k.slice(0, cut));
    if (next && k.slice(cut + 1) >= next) billsPaidAhead[k] = v;
  }
  return { state: { ...state, expenses, recurring, billsPaidAhead }, logged };
}

/* Reconstructs the balance a debt carried on each day of the cycle
   (fromISO, toISO], then returns its average daily balance — the basis real
   issuers charge interest on. `balanceNow` is the debt's balance today; we
   walk the ledger backwards through every entry dated after the cycle
   started to recover the opening balance, then forward again accumulating
   balance × days-held.

   This is the whole point of dating payments: a $3,000 payment made on the
   5th means the cycle carried a low balance for most of the month, and a
   charge on the 28th barely costs anything. Charging today's balance for the
   whole cycle — what this app did before — got both backwards. */
function averageDailyBalance(entries, balanceNow, fromISO, toISO) {
  const span = daysBetween(fromISO, toISO);
  if (span <= 0) return { adb: 0, days: 0, opening: balanceNow };
  /* Rewind to the opening balance by reversing every entry dated after the
     cycle opened (including entries after toISO — they're already baked into
     balanceNow). Reversal is deliberately arithmetic rather than reading each
     row's `balanceBefore`: that snapshot is an absolute figure captured at
     write time, so it goes stale the moment interest is retro-posted into the
     middle of the history. Relative arithmetic composes correctly whatever
     order things were recorded in. */
  let opening = balanceNow;
  for (const p of entries) {
    if (p.date > fromISO) opening += isCreditEntry(p) ? -p.amount : p.amount;
  }
  /* Replay forward, weighting each balance by the days it was held. Entries
     dated on the closing day itself fall inside the cycle. `entries` arrives
     in ledger order, so a stable sort keeps same-day entries in the order
     they were recorded. */
  const within = entries
    .filter(p => p.date > fromISO && p.date <= toISO)
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  let bal = opening, cursor = fromISO, weighted = 0;
  for (const p of within) {
    const held = daysBetween(cursor, p.date);
    if (held > 0) { weighted += bal * held; cursor = p.date; }
    bal = isCreditEntry(p) ? bal + p.amount : bal - p.amount;
  }
  weighted += bal * daysBetween(cursor, toISO);
  return { adb: Math.max(0, weighted / span), days: span, opening };
}

/* Real interest accrual on the actual balance — mirrors processRecurring's
   catch-up pattern above. Interest posts once per statement cycle that has
   fully closed since `lastInterestDate`, computed on the cycle's AVERAGE
   DAILY BALANCE at apr × days/365, and logged as its own entry type (see the
   Debts activity list) so a balance that grows without you touching it is
   always explained, never a silent surprise.

   Cards get a real grace period: if the payments inside the cycle covered
   the previous statement balance, the card was paid in full and nothing is
   charged — exactly what an issuer does. That's detected from the ledger
   rather than asked for, so it can't drift from what actually happened.

   Applies to every debt type with an APR, not just cards — loans already
   accrue interest in every projection the app shows, so real accrual should
   match. A debt with no `lastInterestDate` yet (new, or predating this
   feature) is stamped to the current cycle with nothing backdated — no
   retroactive interest on old data.

   Known simplifications: average daily balance across the cycle rather than
   per-transaction accrual from each purchase date; no cash-advance rate, no
   promotional 0% period, no mid-term rate changes.

   `today` is injectable so the tests can pin a cycle boundary and assert exact
   figures; production always uses the real date. */
function processInterest(state, today = todayISO()) {
  const payments = [...state.payments];
  let posted = 0;
  const debts = state.debts.map(d => {
    const day = statementDayOf(d);
    const thisClose = closeOnOrBefore(today, day);
    /* No bookmark yet: adopt the current cycle and charge nothing for it. */
    if (!d.lastInterestDate) {
      return { ...d, lastInterestDate: thisClose, lastStatementBalance: d.balance, lastInterestMonth: undefined };
    }
    if (d.balance <= 0) {
      return { ...d, lastInterestDate: thisClose, lastStatementBalance: 0, lastInterestMonth: undefined };
    }
    const closes = statementCloses(d.lastInterestDate, today, day);
    if (!closes.length) return d;

    const entries = payments.filter(p => p.debtId === d.id && p.date);
    let bal = d.balance;
    let cycleStart = d.lastInterestDate;
    let statementBalance = d.lastStatementBalance != null ? d.lastStatementBalance : d.balance;
    for (const close of closes) {
      const { adb } = averageDailyBalance(entries, bal, cycleStart, close);
      const paidInCycle = entries
        .filter(p => isPaymentEntry(p) && p.date > cycleStart && p.date <= close)
        .reduce((s, p) => s + p.amount, 0);
      /* Grace period: the statement balance was cleared in full this cycle
         (or there was nothing owed), so no interest is charged. */
      const inGrace = d.type === "card"
        && (statementBalance <= 0.01 || paidInCycle >= statementBalance - 0.01);
      if (!inGrace) {
        const interest = Math.round(adb * ((d.apr || 0) / 100) * (daysBetween(cycleStart, close) / 365) * 100) / 100;
        if (interest >= 0.01) {
          payments.push({ id: uid(), debtId: d.id, amount: interest, date: close, type: "interest", balanceBefore: bal });
          bal = Math.round((bal + interest) * 100) / 100;
          posted++;
        }
      }
      statementBalance = bal;
      cycleStart = close;
    }
    return withAutoMin({ ...d, balance: bal, lastInterestDate: cycleStart, lastStatementBalance: statementBalance, lastInterestMonth: undefined });
  });
  return { state: { ...state, debts, payments }, posted };
}

/* A month on two bases, because the same purchase answers two different
   questions and only one answer can be right for each.

   `spendTotal` is accrual — what you consumed this month, card purchases
   included from the moment they were made. That is what a budget is for:
   "have I overspent on groceries" must not wait for a statement.

   `cashOut` is cash — what actually left the account. A card purchase is
   deliberately NOT here (you owe it, you haven't paid it); the card payment
   that settles it later is. Counting both would bill the same $150 twice.

   Ledger entries are filtered through `isPaymentEntry`, which is the fix for
   a live bug: `state.payments` also holds charges and posted interest, and
   summing the lot inflated "Out" by money that never left the account. */
function monthCashFlow(state, monthKey, monthlyIncome) {
  const inMonth = (d) => d?.slice(0, 7) === monthKey;
  const expenses = (state.expenses || []).filter(e => inMonth(e.date));
  const spendTotal = expenses.reduce((s, e) => s + e.amount, 0);
  // e.debtId marks an expense funded by a card charge — real spending, but
  // not cash out until the card itself is paid.
  const cashSpend = expenses.reduce((s, e) => s + (e.debtId ? 0 : e.amount), 0);
  const debtPaid = (state.payments || [])
    .filter(p => inMonth(p.date) && isPaymentEntry(p))
    .reduce((s, p) => s + p.amount, 0);
  const cashOut = cashSpend + debtPaid;
  return { spendTotal, cashSpend, debtPaid, cashOut, cashLeft: monthlyIncome - cashOut };
}

/* Every bill occurrence due within the next `horizon` days, soonest first.

   A weekly bill really does land four or five times in a 30-day window, and
   each one is money that has to be found — emitting only `nextDue` made the
   planner (and its set-aside total) understate a weekly commitment roughly
   fourfold. Rows carry `occurrenceKey` because `id` alone no longer
   identifies a row once the same bill appears several times. */
function upcomingBills(state, horizon = 30) {
  const today = todayISO();
  const out = [];
  for (const r of state.recurring || []) {
    if (!r.nextDue) continue;
    let due = r.nextDue;
    // Bounded: 30 days of a weekly bill is 5 rows, but an unrecognised freq
    // would otherwise never advance and spin here forever.
    for (let guard = 0; guard < 64; guard++) {
      const days = daysBetween(today, due);
      if (days > horizon) break;
      if (days >= 0) out.push({ ...r, nextDue: due, days, occurrenceKey: `${r.id}:${due}` });
      const nextDue = advanceDue(due, r.freq);
      // null = a one-off that has now been listed; a non-advancing date means a
      // malformed freq. Either way there is nothing further to emit.
      if (!nextDue || nextDue <= due) break;
      due = nextDue;
    }
  }
  return out.sort((a, b) => a.days - b.days);
}

/* Debt minimums due within the next `horizon` days, shaped like an upcoming
   bill so the planner and the reminders can treat both the same way. A debt
   only appears once it has a due date and a balance left to clear.

   `covered` marks a minimum already met inside the current statement cycle —
   read from the ledger rather than a "paid ahead" tick, because for a debt we
   actually know. It keeps a paid minimum out of the set-aside total and out
   of the reminders. */
function upcomingDebtPayments(state, horizon = 30) {
  const today = todayISO();
  const out = [];
  for (const d of state.debts || []) {
    if (d.dueDay == null || d.balance <= 0) continue;
    const nextDue = nextDayOfMonth(d.dueDay, today);
    const days = daysBetween(today, nextDue);
    if (days < 0 || days > horizon) continue;
    const cycleStart = d.lastInterestDate || closeOnOrBefore(today, statementDayOf(d));
    const paidInCycle = (state.payments || [])
      .filter(p => p.debtId === d.id && isPaymentEntry(p) && p.date > cycleStart && p.date <= today)
      .reduce((s, p) => s + p.amount, 0);
    const amount = Math.min(d.minPayment || 0, d.balance);
    out.push({
      id: d.id, debtId: d.id, name: d.name, amount, nextDue, days,
      isDebt: true, debtType: d.type, paidInCycle,
      covered: amount > 0 && paidInCycle >= amount - 0.01,
    });
  }
  return out.sort((a, b) => a.days - b.days);
}

/* Next occurrence of the user's payday (day of month, clamped to 1–28
   so it exists in every month). */
function nextPaydayDate(payday) {
  const day = Math.min(28, Math.max(1, Number(payday) || 20));
  const now = new Date();
  now.setHours(12, 0, 0, 0);
  const p = new Date(now.getFullYear(), now.getMonth(), day, 12);
  if (p <= now) p.setMonth(p.getMonth() + 1);
  return p;
}

/* Single-debt projection at minimum payment only. Returns null when the
   minimum can never pay the debt off (min \u2264 the first month's interest,
   or no minimum at all) \u2014 the negative-amortization trap. */
function minOnlyProjection(debt) {
  const start = debt.balance;
  if (start <= 0) return { months: 0, interest: 0, total: 0 };
  const r = (debt.apr || 0) / 100 / 12;
  if (!debt.minPayment || debt.minPayment <= start * r) return null;
  let bal = start, months = 0, interest = 0;
  while (bal > 0 && months < 1200) {
    const i = bal * r;
    interest += i;
    bal = bal + i - Math.min(debt.minPayment, bal + i);
    months++;
  }
  if (bal > 0) return null; // guard tripped \u2014 treat as never
  return { months, interest, total: start + interest };
}

/* Month keys (YYYY-MM) — the Spend tab’s history navigator. */

const monthKeyOf = (d) => localISO(d).slice(0, 7);
const shiftMonthKey = (key, delta) => {
  const d = new Date(key + "-15T12:00");
  d.setMonth(d.getMonth() + delta);
  return monthKeyOf(d);
};

/* ── exports ─────────────────────────────────────────────────────────────── */
export {
  /* auto-minimum */
  AUTO_MIN_PCT, AUTO_MIN_FLOOR, autoMinPayment, withAutoMin,
  /* dates */
  monthName, addMonths, pad2, localISO, todayISO, dateISO, parseISO, daysBetween,
  monthKeyOf, shiftMonthKey, nextDayOfMonth, nextPaydayDate, advanceDue,
  /* misc helpers */
  uid, FREQ_FACTOR, FREQS, monthlyIncomeOf, recMonthly,
  /* strategies */
  STRATEGIES, otherStrategy, byStrategy,
  /* statement cycles */
  CYCLE_MAX_DAY, STATEMENT_LEAD_DAYS, clampDay, wrapDay, derivedStatementDay,
  statementDayOf, closeOnOrBefore, statementCloses,
  /* ledger */
  isCreditEntry, isPaymentEntry, balanceBeforeEntry, normalizeDebt,
  /* engine */
  averageDailyBalance, processInterest, processRecurring,
  simulate, minOnlyProjection, derivedStartingDebt, moneyMap, project, monthCashFlow,
  EMPTY_SCENARIO, applyLump, whatIf,
  schemeUsable, homePlan, allocate,
  ESSENTIAL_CATEGORIES, essentialMonthly, emergencyTarget, nextStep, budgetTotal, totalOwed,
  /* due dates */
  upcomingBills, upcomingDebtPayments,
};
